import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';

import styles from './styles/ImageViewer.module.css';

const ImageViewer = ({ items, startIndex = 0, onClose, src, alt }) => {
    // Backward compatibility: allow single-image usage via src/alt
    const imageItems = useMemo(() => {
        if (Array.isArray(items) && items.length > 0) {
            return items;
        }
        if (src) {
            return [{ src, alt: alt || '' }];
        }
        return [];
    }, [items, src, alt]);

    const [currentIndex, setCurrentIndex] = useState(
        Math.min(Math.max(0, startIndex), Math.max(0, imageItems.length - 1))
    );
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setCurrentIndex(Math.min(Math.max(0, startIndex), Math.max(0, imageItems.length - 1)));
        setIsLoading(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageItems.length, startIndex]);

    const goPrev = useCallback(() => {
        if (imageItems.length <= 1) return;
        setCurrentIndex((prev) => (prev - 1 + imageItems.length) % imageItems.length);
        setIsLoading(true);
    }, [imageItems.length]);

    const goNext = useCallback(() => {
        if (imageItems.length <= 1) return;
        setCurrentIndex((prev) => (prev + 1) % imageItems.length);
        setIsLoading(true);
    }, [imageItems.length]);

    const handleClickOutside = useCallback((e) => {
        if (e.target.classList.contains(styles.overlay)) {
            onClose();
        }
    }, [onClose]);

    // Keyboard controls: Escape closes; Left/Right navigate
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') {
                onClose();
            } else if (e.key === 'ArrowLeft') {
                goPrev();
            } else if (e.key === 'ArrowRight') {
                goNext();
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [goNext, goPrev, onClose]);

    // Basic swipe support for mobile
    const touchStartXRef = useRef(null);
    const touchStartYRef = useRef(null);

    const onTouchStart = useCallback((e) => {
        if (e.touches && e.touches.length === 1) {
            touchStartXRef.current = e.touches[0].clientX;
            touchStartYRef.current = e.touches[0].clientY;
        }
    }, []);

    const onTouchEnd = useCallback((e) => {
        const startX = touchStartXRef.current;
        const startY = touchStartYRef.current;
        touchStartXRef.current = null;
        touchStartYRef.current = null;
        if (startX == null || startY == null) return;

        const touch = e.changedTouches && e.changedTouches[0];
        if (!touch) return;
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;
        const horizontal = Math.abs(dx) > Math.abs(dy);
        const threshold = 40; // px
        if (horizontal && Math.abs(dx) > threshold) {
            if (dx > 0) {
                goPrev();
            } else {
                goNext();
            }
        }
    }, [goNext, goPrev]);

    const active = imageItems[currentIndex] || imageItems[0];

    // Prefetch neighbors to reduce perceived latency on navigation
    useEffect(() => {
        if (!active) return;
        const preload = (src) => {
            if (!src) return;
            const img = new Image();
            img.src = src;
        };
        const nextIdx = (currentIndex + 1) % imageItems.length;
        const prevIdx = (currentIndex - 1 + imageItems.length) % imageItems.length;
        if (imageItems.length > 1) {
            preload(imageItems[nextIdx]?.src);
            preload(imageItems[prevIdx]?.src);
        }
    }, [active, currentIndex, imageItems]);

    const handleDownload = useCallback(() => {
        if (!active) return;
        const filename = active.alt && String(active.alt).trim().length > 0
            ? active.alt
            : (() => {
                try {
                    const u = new URL(active.src);
                    return u.pathname.split('/').pop() || 'image';
                } catch (e) {
                    const parts = String(active.src).split('/');
                    return parts[parts.length - 1] || 'image';
                }
            })();

        const link = document.createElement('a');
        link.href = active.src;
        link.setAttribute('download', filename);
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        link.remove();
    }, [active]);

    if (imageItems.length === 0) return null;

    return createPortal(
        <div className={styles.overlay} onClick={handleClickOutside} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <button 
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Close image viewer"
            >
                <X size={24} />
            </button>

            {imageItems.length > 1 && (
                <button
                    className={`${styles.navButton} ${styles.navButtonLeft}`}
                    onClick={goPrev}
                    aria-label="Previous image"
                >
                    <ChevronLeft size={24} />
                </button>
            )}

            <div className={styles.imageContainer}>
                {isLoading && (
                    <div className={styles.loader} aria-live="polite" aria-busy="true" />
                )}
                <img
                    src={active.src}
                    alt={active.alt || ''}
                    className={styles.image}
                    style={{ visibility: isLoading ? 'hidden' : 'visible' }}
                    onLoad={() => setIsLoading(false)}
                    onError={() => setIsLoading(false)}
                />
            </div>

            {imageItems.length > 1 && (
                <button
                    className={`${styles.navButton} ${styles.navButtonRight}`}
                    onClick={goNext}
                    aria-label="Next image"
                >
                    <ChevronRight size={24} />
                </button>
            )}

            {imageItems.length > 1 && (
                <div className={styles.counter} aria-live="polite">
                    {currentIndex + 1} / {imageItems.length}
                </div>
            )}

            <button
                className={styles.downloadButton}
                onClick={handleDownload}
                aria-label="Download image"
            >
                <Download size={19} />
            </button>
        </div>,
        document.body
    );
};

export default memo(ImageViewer);
